import { useSession } from "@/services/auth0/session";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import type { Address } from "@/api.types.v2";
import type { Carrier } from "@/api/carriers";

const API_URL = import.meta.env.VITE_API_URL;

/**
 * PickupBooking admin client (read-only listing + detail).
 *
 * Bookings are not created here — they're created at order time by
 * createUnifiedOrder's find-or-attach logic. Cancel / reschedule / refund
 * actions are deferred to Phase 11+; this client surfaces the data only.
 */

export type PickupBookingDerivedState =
  | "Scheduled"
  | "InProgress"
  | "Completed"
  | "Failed"
  | "Cancelled";

// Explicit list/detail wire fields; do not inherit the legacy generic Booking aliases.
export type AdminPickupBooking = {
  id: string;
  slotId: string;
  scheduledDate: string;
  cancelledAt: string | null;
  notes: string | null;
  pickupAddress: Address;
  carrierAddOnService: { id: string; code: string; name: string };
  dispatchCenter: {
    id: string;
    code: string;
    name: string;
    // Actual pickup-provider relation returned by list and detail endpoints.
    pickupProviderCarrier?: Pick<Carrier, "id" | "code" | "name"> | null;
  };
  timingAvailability?: "CAPTURED" | "UNKNOWN_LEGACY";
  timeZone?: string | null;
  schedulingTimeZone?: string | null;
  slot: { id: string; name: string; startTime: string; endTime: string } | null;
  shipments: {
    id: string;
    shipmentNumber: number;
    trackingNumber: string | null;
    status: string;
    pickupPriceCents: number | null;
    carrier: Pick<Carrier, "id" | "code" | "name"> | null;
    /** CUSTOMER_PICKUP tasks only — used to derive booking state. */
    tasks: { id: string; status: string }[];
    order: {
      placerCustomer: {
        id: string;
        email: string;
        firstName: string | null;
        lastName: string | null;
      } | null;
    } | null;
  }[];
  /** Computed server-side from booking.status + tasks across active shipments. */
  derivedState: PickupBookingDerivedState;
  /** Non-cancelled shipment count — what's actually riding on this run. */
  attachedShipmentCount: number;
  totalShipmentCount: number;
  /** Distinct placer customers across all attached shipments. */
  customers: { id: string; name: string }[];
  /** Phase 12 (#232): sum of |amountCents| across DISCOUNT-type
   *  PriceAdjustments scoped to this booking. 0 when no shipment was discounted. */
  bundleDiscountCents: number;
  /** Decorated list value; the detail endpoint does not supply tax. */
  taxCents?: number | null;
};

export function useGetPickupBookings(params?: {
  state?: PickupBookingDerivedState;
  dispatchCenterId?: string;
  fromDate?: string;
  toDate?: string;
  limit?: number;
}) {
  const { token } = useSession();

  return useQuery({
    queryKey: ["pickup-bookings", params],
    queryFn: async () => {
      const q = new URLSearchParams();
      if (params?.state) q.append("state", params.state);
      if (params?.dispatchCenterId)
        q.append("dispatchCenterId", params.dispatchCenterId);
      if (params?.fromDate) q.append("fromDate", params.fromDate);
      if (params?.toDate) q.append("toDate", params.toDate);
      if (params?.limit) q.append("limit", String(params.limit));

      const { data } = await axios.get<{
        success: boolean;
        data: AdminPickupBooking[];
      }>(
        `${API_URL}/api/v2/pickup-bookings${q.toString() ? `?${q.toString()}` : ""}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data.data;
    },
    enabled: !!token,
  });
}

export function useGetPickupBookingById(id: string | null) {
  const { token } = useSession();

  return useQuery({
    queryKey: ["pickup-booking", id],
    queryFn: async () => {
      const { data } = await axios.get<{
        success: boolean;
        data: AdminPickupBooking;
      }>(`${API_URL}/api/v2/pickup-bookings/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return data.data;
    },
    enabled: !!token && !!id,
  });
}

/**
 * Phase 11a: admin actions — cancel + detach + notes update + print.
 * All routed through the same shared cancellation service the customer
 * endpoints hit, so the cascade rules (PENDING task cancel, last-shipment
 * auto-cancel) stay identical.
 */

export function useCancelPickupBookingAdmin() {
  const queryClient = useQueryClient();
  const { token } = useSession();

  return useMutation({
    mutationFn: async ({
      bookingId,
      notes,
    }: {
      bookingId: string;
      notes?: string | null;
    }) => {
      const { data } = await axios.post<{
        success: boolean;
        data: AdminPickupBooking;
      }>(
        `${API_URL}/api/v2/pickup-bookings/${bookingId}/cancel`,
        {
          ...(notes !== undefined && { notes }),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data.data;
    },
    onSuccess: async (_, { bookingId }) => {
      await queryClient.invalidateQueries({ queryKey: ["pickup-bookings"] });
      await queryClient.invalidateQueries({
        queryKey: ["pickup-booking", bookingId],
      });
    },
  });
}

export function useUpdatePickupBookingNotesAdmin() {
  const queryClient = useQueryClient();
  const { token } = useSession();

  return useMutation({
    mutationFn: async ({
      bookingId,
      notes,
    }: {
      bookingId: string;
      notes: string | null;
    }) => {
      const { data } = await axios.patch<{
        success: boolean;
        data: AdminPickupBooking;
      }>(
        `${API_URL}/api/v2/pickup-bookings/${bookingId}`,
        { notes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data.data;
    },
    onSuccess: async (_, { bookingId }) => {
      await queryClient.invalidateQueries({ queryKey: ["pickup-bookings"] });
      await queryClient.invalidateQueries({
        queryKey: ["pickup-booking", bookingId],
      });
    },
  });
}

export function useDetachShipmentFromPickupBookingAdmin() {
  const queryClient = useQueryClient();
  const { token } = useSession();

  return useMutation({
    mutationFn: async ({
      bookingId,
      shipmentId,
      reason,
    }: {
      bookingId: string;
      shipmentId: string;
      reason?: string;
    }) => {
      const { data } = await axios.delete<{
        data: { booking: AdminPickupBooking };
      }>(
        `${API_URL}/api/v2/pickup-bookings/${bookingId}/shipments/${shipmentId}`,
        {
          data: { reason },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return data.data;
    },
    onSuccess: async (_, { bookingId }) => {
      await queryClient.invalidateQueries({ queryKey: ["pickup-bookings"] });
      await queryClient.invalidateQueries({
        queryKey: ["pickup-booking", bookingId],
      });
    },
  });
}

/**
 * Phase 11c slice 3: admin reschedule (commit-only — same-tier in v1).
 * Server rejects with PRICE_CHANGED when the re-quote produces a delta;
 * surface the error so admin knows to cancel + recreate instead.
 */
export interface AdminReschedulePreview {
  /** Server 3e26dc49: target proposal, never the source Booking window. Old replay receipts may omit it. */
  proposedTiming?: {
    scheduledDate: string;
    slotId: string;
    timingAvailability: "CAPTURED" | "UNKNOWN_LEGACY";
    slot: {
      id: string;
      name: string;
      startTime: string;
      endTime: string;
    } | null;
    timeZone: string | null;
  };

  previewFingerprint: string;
  deliveryOutcomes: Array<{
    shipmentId: string;
    deliveredFastest: string;
    deliveredSlowest: string;
    deliveredPrecision: "DATE" | "DATETIME";
    timeZone: string;
    locationSummary?: { dispatch: string; dropoff: string | null };
  }>;
  oldTotalCents: number;
  newTotalCents: number;
  deltaCents: number;
  perShipment: {
    id: string;
    shipmentNumber: number;
    oldPickupPriceCents: number;
    newPickupPriceCents: number;
  }[];
  conflictingBookingId?: string;
}

interface AdminRescheduleArgs {
  expectedPreviewFingerprint?: string;
  acceptCurrentLocationConfiguration?: boolean;
  bookingId: string;
  newScheduledDate: string;
  newSlotId: string;
  newPickupServiceId?: string;
  shipmentIds: string[];
}

export function useReschedulePickupBookingAdmin() {
  const queryClient = useQueryClient();
  const { token } = useSession();

  return useMutation({
    mutationFn: async (args: AdminRescheduleArgs) => {
      const { bookingId, ...body } = args;
      if (!body.expectedPreviewFingerprint) {
        const { data } = await axios.post<{ data: AdminReschedulePreview }>(
          `${API_URL}/api/v2/pickup-bookings/${bookingId}/reschedule/preview`,
          body,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        return { ...data.data, booking: undefined };
      }
      const { data } = await axios.post<{
        data: { booking: AdminPickupBooking } & AdminReschedulePreview;
      }>(
        `${API_URL}/api/v2/pickup-bookings/${bookingId}/reschedule`,
        {
          ...body,
          expectedPreviewFingerprint: body.expectedPreviewFingerprint,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return data.data;
    },
    onSuccess: async (_, { bookingId }) => {
      await queryClient.invalidateQueries({ queryKey: ["pickup-bookings"] });
      await queryClient.invalidateQueries({
        queryKey: ["pickup-booking", bookingId],
      });
    },
  });
}

/** 422-with-error-code from reschedule endpoint. */
export function isAdminRescheduleStructuredError(
  err: unknown
): err is import("axios").AxiosError<{
  error: string;
  message?: string;
  details?: Record<string, unknown>;
}> {
  if (!axios.isAxiosError(err)) return false;
  const data: unknown = err.response?.data;
  return (
    typeof data === "object" &&
    data !== null &&
    "error" in data &&
    typeof (data as { error: unknown }).error === "string"
  );
}

export function usePrintPickupBookingAdmin() {
  const { token } = useSession();

  return useMutation({
    mutationFn: async (bookingId: string) => {
      const response = await axios.get(
        `${API_URL}/api/v2/pickup-bookings/${bookingId}/print`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob",
        }
      );
      return { blob: response.data as Blob, bookingId };
    },
    onSuccess: ({ blob, bookingId }) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `pickup-${bookingId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    },
  });
}
